import React, { ReactElement, useMemo } from 'react';
import { baseTheme } from '@feast-it/ui/light';
import { useBreakpoint } from '../../../hooks/media';
import { PageProperties, SupplierSection } from '../../../types';
import { SliceLayout } from '../../../components/SliceLayout/SliceLayout';
import { SuppliersSkeleton } from './SuppliersSkeleton';
import { hasItemsInArray } from '../../../utils/array';
import { SuppliersHeader } from './SuppliersHeader';
import { SuppliersGrid } from './SuppliersGrid';
import { Container } from './SuppliersV2.styled';

interface SuppliersProps {
  suppliers: SupplierSection;
  nearMeCity: string;
  isLoading: boolean;
  pageProperties: PageProperties;
}

export function SuppliersV2({
  suppliers,
  nearMeCity,
  isLoading,
  pageProperties,
}: SuppliersProps): ReactElement {
  const isDesktop = useBreakpoint(baseTheme.breakpoints.medium);
  const results = useMemo(() => {
    if (!isDesktop && suppliers?.outOfRange && suppliers?.results) {
      return suppliers.results.slice(0, 2);
    }
    return suppliers?.results || [];
  }, [isDesktop, suppliers]);

  const { pageContent, locationVariable } = pageProperties;
  const { isCity, isNearMe, name } = locationVariable || {};
  const amountOfSuppliers = suppliers.results?.length || 0;
  const showLocation = (isCity || isNearMe) && !suppliers?.outOfRange;
  const locationName = isNearMe ? nearMeCity : name;

  if (!isLoading && !hasItemsInArray(results)) {
    return null;
  }

  return (
    <SliceLayout backgroundColor="lightGrey">
      <Container>
        {isLoading && (!results || !hasItemsInArray(results)) && (
          <SuppliersSkeleton />
        )}

        {!isLoading && hasItemsInArray(results) && (
          <>
            {!suppliers?.outOfRange && (
              <SuppliersHeader
                pageContent={pageContent}
                amountOfSuppliers={amountOfSuppliers}
              />
            )}

            <SuppliersGrid
              results={results}
              showLocation={showLocation}
              locationName={locationName}
            />
          </>
        )}
      </Container>
    </SliceLayout>
  );
}
